/**
 * Marketing -> Overview. The section's index page.
 *
 * THE FIRST THING IT SAYS IS WHY NOTHING IS GOING OUT. Every figure in this
 * module is zero until a sending channel exists and somebody signs off on the
 * first letter, and a screen full of zeroes reads as broken software rather
 * than as a module waiting on a decision. So the top of the page is a sentence
 * naming the gate the SENDER would refuse at - taken from the same preflight
 * list, in the same order - and the numbers come after it.
 *
 * THE NUMBERS COME FROM THE LEDGER, NOT FROM THE CAMPAIGN LIST'S CACHE. The two
 * should agree, and when they do not this page says so instead of quietly
 * disagreeing with the screen next to it. `MktCampaign`'s eight counter columns
 * are recounted from the ledger on every event that touches it; a gap means one
 * of those recounts did not happen.
 *
 * The order of the cards is the order of the questions: may we send, how much
 * have we sent, is the domain healthy, what does the portfolio look like, where
 * is every address in the ledger, how much of the market can we even see, and
 * what has the sender done lately.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { StaffUser } from "../../../../lib/auth";
import { getMarketingOverview, type MarketingOverview } from "../../../../lib/marketingOverview";
import { PageHead } from "../../../ui";
import { Note, Panel, num } from "../shared";
import { CampaignMixCard } from "./CampaignMixCard";
import { GateNote } from "./GateNote";
import { HeadlineCards } from "./HeadlineCards";
import { HealthCard } from "./HealthCard";
import { LedgerCard } from "./LedgerCard";
import { RunsCard } from "./RunsCard";
import { SectorCard } from "./SectorCard";
import { SendChart } from "./SendChart";
import { contactedDrift } from "./lib";

export function OverviewPage({ user: _user }: { user: StaffUser }) {
  const t = useT();
  const [view, setView] = useState<MarketingOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    getMarketingOverview()
      .then(setView)
      .catch(() => setView(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const drift = view ? contactedDrift(view.ledger, view.cached) : null;

  return (
    <>
      <PageHead
        title={t("marketing.overview.title")}
        sub={t("marketing.overview.intro")}
        actions={
          <Link to="/marketing/campaigns" className="crm-btn">
            {t("nav.campaigns")}
          </Link>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : !view ? (
        <Panel title={t("marketing.overview.title")}>
          <p className="crm-sub">{t("marketing.error.load")}</p>
        </Panel>
      ) : (
        <>
          <GateNote view={view} />

          {drift ? (
            <Note tone="warn" icon={<AlertTriangle className="size-3.5" />} className="mb-3.5">
              {t("marketing.overview.drift", { ledger: num(drift.ledger), cached: num(drift.cached) })}
            </Note>
          ) : null}

          <HeadlineCards ledger={view.ledger} daily={view.daily} />

          <div className="space-y-4">
            <SendChart daily={view.daily} windowDays={view.dailyWindowDays} timezone={view.timezone} />

            <div className="grid gap-4 xl:grid-cols-2">
              <HealthCard health={view.health} />
              <CampaignMixCard campaigns={view.campaigns} suppression={view.suppression} />
            </div>

            <LedgerCard ledger={view.ledger} />
            {/* The register comparison sits below the send numbers on purpose.
                It answers "how well can we even see this market", which is the
                question an operator has AFTER the ones about their own
                sending - and it is the one card here that says nothing about
                what this module did. Absent when the mirror could not be
                counted, rather than drawn as a row of zeroes. */}
            {view.sectors ? <SectorCard view={view.sectors} /> : null}
            <RunsCard runs={view.runs} />
          </div>
        </>
      )}
    </>
  );
}
